/**
 * DetailErrorBoundary — class-based error boundary wrapping every detail
 * panel so an enrichment / render failure can't take down the whole app.
 *
 * The failure state is styled here rather than through ui/Modal on
 * purpose: DetailPanel is eagerly imported by App.tsx, so reaching for
 * Modal would pull the whole modal primitive into the main chunk to
 * serve a path almost nobody hits. It borrows the dialog semantics and
 * the visual language instead.
 *
 * Also exports SafeSection — a per-section sub-boundary used inside the
 * heavier detail panels (Heritage, Building, etc.) so a single broken
 * section degrades gracefully without unmounting the whole panel.
 */
import React, { Component, useRef } from "react";
import { AlertCircle } from "lucide-react";
import type { Lang } from "../types/index.ts";
import { t } from "../i18n/index.ts";
import { useFocusTrap } from "../hooks/useFocusTrap.ts";
import { useFocusRestore } from "../hooks/useFocusRestore.ts";
import s from "./DetailErrorBoundary.module.css";

/**
 * Write the failure down where it can be found again.
 *
 * This boundary used to call console.error and stop there, which on a phone is
 * the same as saying nothing: a reader saw "an error occurred", sent a
 * screenshot, and neither the screenshot nor the app could say which place had
 * failed or what threw. RootErrorBoundary already persisted its crashes to
 * `fornland-last-error`; this one now does too, and adds the pin and the layer,
 * because "a megalith in Hurum" is what a reader can tell you and an id is
 * what you can act on.
 *
 * Same key as the root boundary on purpose. The last error is the last error,
 * and `where` says which of the two wrote it.
 */
export function recordFailure(where: "detail" | "section", error: Error, componentStack: string, extra: Record<string, unknown>) {
  try {
    localStorage.setItem("fornland-last-error", JSON.stringify({
      ts: new Date().toISOString(),
      where,
      ...extra,
      message: error.message,
      stack: error.stack,
      componentStack,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      url: typeof location !== "undefined" ? location.href : "",
    }));
  } catch { /* quota or private-mode failure — a boundary must not throw */ }
}

/**
 * The failure state, split out of the class so it can use the focus hooks.
 * A boundary that fires while the reader is somewhere inside the panel it
 * just replaced would otherwise leave focus on a node that no longer exists,
 * and this dialog says aria-modal, so everything outside it is hidden from
 * their software. The single close button is the whole cycle.
 */
function ErrorDialog({ onClose, lang }: { onClose: () => void; lang: Lang }) {
  const ref = useRef<HTMLDivElement>(null);
  useFocusRestore();
  useFocusTrap(ref);
  return (
    <>
      <div onClick={onClose} aria-hidden="true" className={s.backdrop} />
      <div ref={ref} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="detail-error-title" className={s.sheet}>
        <AlertCircle size={22} stroke="var(--text-tertiary)" strokeWidth={1.5} aria-hidden="true" />
        <h2 id="detail-error-title" className={s.title}>{t("error.loadFailed", lang)}</h2>
        <p className={s.text}>{t("error.tryAgain", lang)}</p>
        <button onClick={onClose} className={s.closeBtn}>{t("error.close", lang)}</button>
      </div>
    </>
  );
}

export class DetailErrorBoundary extends Component<{
  onClose: () => void;
  lang?: Lang;
  /** The pin that failed, so the record names a place and not just a stack. */
  siteId?: string;
  layer?: string;
  children: React.ReactNode;
}, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[Fornland] Detail panel error:", error, info.componentStack);
    recordFailure("detail", error, info.componentStack ?? "", { siteId: this.props.siteId, layer: this.props.layer });
  }
  render() {
    if (this.state.hasError) {
      return <ErrorDialog onClose={this.props.onClose} lang={this.props.lang ?? "da"} />;
    }
    return this.props.children;
  }
}

export class SafeSection extends Component<{ fallback?: React.ReactNode; name?: string; children: React.ReactNode }, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.warn("[Fornland] Section render error:", error.message);
    recordFailure("section", error, info.componentStack ?? "", { section: this.props.name });
  }
  render() {
    if (this.state.hasError) return this.props.fallback ?? null;
    return this.props.children;
  }
}

export function SectionError({ lang }: { lang: Lang }) {
  return (
    <div className={s.sectionError}>
      <p className={s.sectionErrorText}>{t("error.sectionFailed", lang)}</p>
    </div>
  );
}
